'use client'

// imports
import { myListShows } from '@/lib/resources/my-list-shows';
import { usePersonalizedResource } from '@/lib/hooks/usePersonalizedResource';
import { myListVideos } from '@/lib/resources/my-list-videos';
import Cookies from 'js-cookie';
import { useAtom } from 'jotai';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { useSearchParams, usePathname, useRouter } from 'next/navigation';

// lib files
import {
  addFavoriteShow,
  addFavoriteVideo,
} from '@/lib/helpers/favorites';
import { sendPbsGtmEvent, EventData } from "@/lib/helpers/send-pbs-gtm-event";
import { ShowRowContent, ShowDetails } from '@/lib/types/api/show-data';
import { TMP_QP_VIDEO_MY_LIST_RETURN_COOKIE, TMP_QP_SHOW_MY_LIST_RETURN_COOKIE } from '@/lib/constants';
import { useIdentity } from '@/lib/hooks/useIdentity';
import { VideoClass, VideoTypeEnum } from '@/lib/types/api/video';
import { signinModalAtom, SignInModalStateEnum } from '@/lib/atoms/signin-modal';

// components
import MyListQueryParamPopup from '@/components/MyList/MyListQueryParamPopup/MyListQueryParamPopup';

interface NetworkMyListQueryParamHandlerProps {
  video?: {
    slug: string;
    cid: string;
    title: string;
    legacy_tp_media_id?: number;
    video_type?: VideoTypeEnum;
    show?: {
      title: string;
    }
    franchise?: {
      title: string;
    }
  };
  show?: ShowRowContent | ShowDetails;
}

const baseEventData: EventData = {
  feature_category: "my list",
  feature_name: "my list query parameter",
  signed_in_status: true,
}

function NetworkMyListQueryParamHandler(props: NetworkMyListQueryParamHandlerProps) {
  const { video, show } = props;

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const [ myListShowsData, setMyListShowsData ] = useAtom(myListShows.itemsAtom);
  const [ myListVideosData, setMyListVideosData ] = useAtom(myListVideos.itemsAtom);
  const [ shouldAddToMyList, setShouldAddToMyList ] = useState(false);
  const { isSettled: showsLoaded } = usePersonalizedResource(myListShows);
  const { isSettled: videosLoaded } = usePersonalizedResource(myListVideos);
  const { isSignedIn } = useIdentity();
  const [_, setSigninModalOpen] = useAtom(signinModalAtom);
  const [ isPopupOpen, setIsPopupOpen ] = useState(false);
  const [ popupText, setPopupText ] = useState<ReactNode>('');

  const handleAddShowToList = useCallback(() => {
    if (!show) return;
    const shows = myListShowsData ?? [];
    const alreadyInList = shows.some(s => s.slug === show.slug);
    if (alreadyInList) {
      setIsPopupOpen(true);
      setPopupText(<>&ldquo;{show.title}&rdquo; is already in <Link href="/my-list/shows/">My List</Link></>);
      return;
    }

    setMyListShowsData([show, ...shows]);
    (async () => {
      try {
        await addFavoriteShow(show.cid);
        Cookies.remove(TMP_QP_SHOW_MY_LIST_RETURN_COOKIE);
        const eventData: EventData = {
          ...baseEventData,
          franchise_title: show.franchise?.title,
          object_action_behavior: "adds show to list after sign in - because of query param",
          object_name: "my list - add show to list",
          pbs_content_id: show.slug,
          show_title: show.title,
        }
        sendPbsGtmEvent(eventData);
        setIsPopupOpen(true);
        setPopupText(<>&ldquo;{show.title}&rdquo; has been added to <Link href="/my-list/shows/">My List</Link>!</>);
      } catch (_error) {
        setMyListShowsData(shows);
        setIsPopupOpen(true);
        setPopupText(`Something went wrong adding "${show.title}" to My List. Try clicking the button (+).`);
      }
    })();
  }, [myListShowsData, setMyListShowsData, show]);

  const handleAddVideoToList = useCallback(() => {
    if (!video) return;
    const videos = myListVideosData ?? [];
    const alreadyInList = videos.some(v => v.slug === video.slug);
    const showAndVideoTitle = video.show ? `${video.show.title}: ${video.title}` : video.title;
    if (alreadyInList) {
      setIsPopupOpen(true);
      setPopupText(<>&ldquo;{showAndVideoTitle}&rdquo; is already in <Link href="/my-list/videos/">My List</Link></>);
      return;
    }

    setMyListVideosData([video as VideoClass, ...videos]);
    (async () => {
      try {
        await addFavoriteVideo(video.cid);
        Cookies.remove(TMP_QP_VIDEO_MY_LIST_RETURN_COOKIE);
        const eventData: EventData = {
          ...baseEventData,
          franchise_title: video.franchise?.title,
          object_action_behavior: "adds video to list after sign in - because of query param",
          object_name: "my list - add video to list",
          pbs_content_id: video.slug,
          show_title: video.show?.title,
          video_title: video.title,
          video_tp_media_id: video.legacy_tp_media_id,
          video_type: video.video_type,
        }
        sendPbsGtmEvent(eventData);
        setIsPopupOpen(true);
        setPopupText(<>&ldquo;{showAndVideoTitle}&rdquo; has been added to <Link href="/my-list/videos/">My List</Link>!</>);
      } catch (_error) {
        setMyListVideosData(videos);
        setIsPopupOpen(true);
        setPopupText(`Something went wrong adding "${showAndVideoTitle}" to My List. Try clicking the button (+).`);
      }
    })();
  }, [myListVideosData, setMyListVideosData, video]);

  // This useEffect runs on initial page load to check for the presence of the ?addToMyList=true query param.
  // If it exists, we:
  // - remove it from the URL
  // - set a flag to trigger the my list addition flow in the next useEffect
  useEffect(() => {
    // we are very deliberately making this work only when ?addToMyList=true -
    // it *is case sensitive* and must be exactly that value in order to trigger the my list addition flow.
    if (searchParams.get('addToMyList') !== 'true') return;

    const params = new URLSearchParams(searchParams.toString());
    params.delete('addToMyList');
    const newUrl = params.size > 0 ? `${pathname}?${params.toString()}` : pathname;
    router.replace(newUrl, { scroll: false });
    setShouldAddToMyList(true);
  // This effect should only run once on mount to handle the query param on initial page load
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // This useEffect runs when the shouldAddToMyList flag is set to true (which happens in the previous useEffect when we detect the query param on page load).
  // We check if the user is logged in. If they are, we add the show/video to their list immediately (if the relevant data is loaded).
  // If they are not logged in, we:
  // - open the sign in modal
  // - set a temporary cookie to handle adding the show/video to their list after they successfully log in and return to the page
  useEffect(() => {
    const isLoggedIn = isSignedIn;
    // isSettled: false while idle as well as while loading, so we never decide
    // an item is absent before the list has arrived.

    if (shouldAddToMyList) {
      if (isLoggedIn) {
        if (show && !video && showsLoaded) {
          handleAddShowToList();
          setShouldAddToMyList(false);
        }

        if (video && !show && videosLoaded) {
          handleAddVideoToList();
          setShouldAddToMyList(false);
        }
      } else {
        setSigninModalOpen(SignInModalStateEnum.TrueWithMyListBlurb);
        // user is not logged in - setting tmp cookies
        if (show && !video) {
          // 1/288 is 5 minutes - that is, 1/288th of a day
          Cookies.set(TMP_QP_SHOW_MY_LIST_RETURN_COOKIE, show.cid, { expires: 1/288 });
        } else if (video && !show) {
          Cookies.set(TMP_QP_VIDEO_MY_LIST_RETURN_COOKIE, video.cid, { expires: 1/288 });
        }
      };
    };
  }, [
    handleAddShowToList,
    handleAddVideoToList,
    myListShowsData,
    myListVideosData,
    isSignedIn,
    setMyListShowsData,
    setMyListVideosData,
    setSigninModalOpen,
    shouldAddToMyList,
    show,
    showsLoaded,
    video,
    videosLoaded,
  ]);

  // This useEffect handles the case where the user was not originally logged in, was prompted to log in,
  // and has now successfully returned from the login flow. We check for the presence of the temporary cookies
  // we set before to determine if we need to add the show/video to the user's my list.
  useEffect(() => {
    if (!isSignedIn) return;

    // isSettled: false while idle as well as while loading, so we never decide
    // an item is absent before the list has arrived.

    const showCookieValue = Cookies.get(TMP_QP_SHOW_MY_LIST_RETURN_COOKIE);
    if (show && showCookieValue && showCookieValue === show.cid && showsLoaded) {
      Cookies.remove(TMP_QP_SHOW_MY_LIST_RETURN_COOKIE);
      handleAddShowToList();
    }

    const videoCookieValue = Cookies.get(TMP_QP_VIDEO_MY_LIST_RETURN_COOKIE);
    if (video && videoCookieValue && videoCookieValue === video.cid && videosLoaded) {
      Cookies.remove(TMP_QP_VIDEO_MY_LIST_RETURN_COOKIE);
      handleAddVideoToList();
    }
  }, [
    handleAddShowToList,
    handleAddVideoToList,
    myListShowsData,
    myListVideosData,
    isSignedIn,
    setMyListShowsData,
    setMyListVideosData,
    show,
    showsLoaded,
    video,
    videosLoaded,
  ]);

  return (
  <>
    <MyListQueryParamPopup isOpen={isPopupOpen} text={popupText} />
  </>)
}

export default NetworkMyListQueryParamHandler
